import {Slide, SlideProps} from '@mui/material';
import classnames from 'classnames';
import {
  Form,
  Formik,
  FormikConfig,
  FormikContextType,
  FormikHelpers,
  useFormikContext,
} from 'formik';
import {isEmpty, keyBy} from 'lodash';
import React, {useContext, useEffect, useMemo, useState} from 'react';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import OnboardingFooter from 'src/components/Onboarding/Footer';
import {
  OnboardingContext,
  OnboardingContextType,
  OnboardingProvider,
} from 'src/components/Onboarding/context';
import {
  filterSteps,
  getInitialStep,
  validateStep,
  ValidationResult,
} from 'src/components/Onboarding/utils';
import {StepConfig, StepPosition} from 'src/types/form';
import {PlatformContext} from '../PlatformContext';
import OnboardingProgressBar, {OnboardingProgressBarProps} from './onboarding-progress-bar';
import MobileOnboardingProgressButton from '../form/onboarding/account-suggestion/mobile-onboarding-progress-button';

function CanSubmitListener(props: {step: StepPosition; stepCount: number}): null {
  const onboardingContext = useContext(OnboardingContext);
  const form = useFormikContext();
  const canSubmit = isEmpty(form.errors) && props.step.index + 1 === props.stepCount;
  useEffect(() => {
    onboardingContext.setCanSubmit(canSubmit);
  }, [canSubmit]);

  useEffect(() => {
    form.validateForm();
  }, [props.step.index]);
  return null;
}

export interface OnboardingProps<Values, Actions> {
  steps: StepConfig<Values, Actions>[];
  basePath?: string;
  formikProps: Omit<FormikConfig<Values>, 'onSubmit'>;
  onIncrementalSubmit: (
    values: Values,
    stepPosition: StepPosition,
    helpers: FormikHelpers<Values>
  ) => Promise<boolean>;
  onSubmit: (values: Values, stepPosition: StepPosition) => Promise<void>;
  getValues: () => Promise<Values> | Values;
  getActions: () => Actions;
  transitionTimeout?: number;
  hasEligibilityPage?: boolean;
}

export interface DirectionState {
  enter: SlideProps['direction'];
  exit: SlideProps['direction'];
}

export const forwardDirection: DirectionState = {
  enter: 'left',
  exit: 'right',
};

export const backwardsDirection: DirectionState = {
  enter: 'right',
  exit: 'left',
};

type ProgressbarWrapperComponentProps<Values, Actions> = Pick<
  OnboardingProps<Values, Actions>,
  'steps'
> &
  Pick<
    OnboardingProgressBarProps<Values>,
    'stepCount' | 'stepPosition' | 'decrementStep' | 'onControlledSubmit'
  >;

const ProgressbarWrapperComponent = <Values extends object, Actions>({
  steps,
  stepPosition,
  decrementStep,
  onControlledSubmit,
}: ProgressbarWrapperComponentProps<Values, Actions>) => {
  const {isLoading, isControlledStep} = useContext<OnboardingContextType>(OnboardingContext);
  const formikContext = useFormikContext();

  return (
    <OnboardingProgressBar<Values>
      stepCount={steps.length}
      stepPosition={stepPosition}
      decrementStep={decrementStep}
      isSubmitting={formikContext.isSubmitting}
      hasErrors={!isEmpty(formikContext.errors)}
      invalid={!formikContext.isValid}
      onControlledSubmit={onControlledSubmit}
      isLoading={isLoading}
      isControlledStep={isControlledStep}
    />
  );
};

function Onboarding<Values extends object, Actions>(
  props: RouteComponentProps & OnboardingProps<Values, Actions>
) {
  const [stepPosition, setStepPosition] = useState<StepPosition>();
  const [nextPosition, setNextPosition] = useState<StepPosition>();
  const [direction, setDirection] = useState<DirectionState>();
  const [steps, setSteps] = useState<StepConfig<Values, Actions>[]>();
  const stepsByKey = useMemo(() => keyBy(steps, 'key'), [steps]);
  const platformContext = useContext(PlatformContext);

  const pathToStep = (step: StepConfig<Values, Actions>): string =>
    `${props.basePath}/${step.pathSuffix || step.key}`;

  function updateStepInformation(index: number): void {
    const step = steps?.[index];
    if (!step) return;
    if (stepPosition) {
      setDirection(index < stepPosition.index ? backwardsDirection : forwardDirection);
    }

    setNextPosition({index, key: step.key});

    const path = pathToStep(step);
    props.history.push(path);
  }

  async function setStep(index: number, helpers?: FormikHelpers<Values>): Promise<void> {
    updateStepInformation(index);
    if (!helpers) return;
    const initialValues = await props.getValues();
    helpers.resetForm(initialValues);
  }

  useEffect(() => {
    filterSteps(props.getValues, props.steps).then(setSteps);
  }, [props.steps]);

  useEffect(() => {
    if (!steps) return;
    getInitialStep(steps, props.formikProps.initialValues, props.hasEligibilityPage).then(
      (initialPosition) => {
        const path = pathToStep(steps[initialPosition.index]);
        props.history.push(path);
        setStepPosition(initialPosition);
        setNextPosition(initialPosition);
      }
    );
  }, [steps]);

  if (!stepPosition || !nextPosition || !steps) return null;

  const currentStep = steps[stepPosition.index];

  async function validate(form: Values): ValidationResult<Values> {
    if (!stepPosition) return {};
    const step = stepsByKey[stepPosition.key];
    return validateStep(step, form);
  }

  async function decrementStep(helpers?: FormikHelpers<Values>): Promise<void> {
    if (!stepPosition) return;
    const prevIndex = stepPosition.index - 1;
    return setStep(prevIndex, helpers);
  }

  async function incrementStep(helpers: FormikHelpers<Values>): Promise<void> {
    if (!stepPosition) return;
    const nextIndex = stepPosition.index + 1;
    return setStep(nextIndex, helpers);
  }

  async function onSubmit(values: Values, helpers: FormikHelpers<Values>): Promise<void> {
    if (!stepPosition || !steps) return;
    const isIncremental = stepPosition.index < steps.length - 1;
    if (isIncremental) {
      const shouldProceed = await props.onIncrementalSubmit(values, stepPosition, helpers);
      shouldProceed && (await incrementStep(helpers));
    } else {
      await props.onSubmit(values, stepPosition);
    }
  }

  function skipStep(): void {
    if (!stepPosition) return;
    updateStepInformation(stepPosition.index + 1);
  }

  function onTransitionComplete(): void {
    setStepPosition(nextPosition);
  }

  async function onControlledSubmit(
    form: FormikContextType<Values>,
    currentStepPosition: StepPosition
  ): Promise<void> {
    const step = steps && steps[currentStepPosition.index];
    if (step && step.onControlledSubmit) {
      await step.onControlledSubmit(form, props.getActions(), skipStep);
    } else {
      console.debug(`Onboarding.steps[${currentStepPosition.key}].onControlledSubmit() missing`);
    }
  }

  const isStepActive = stepPosition.index === nextPosition.index;

  const step = steps?.[stepPosition.index];
  let initialValues = props.formikProps.initialValues;

  // all of our onboardings actually provide initialValues but TS gets
  // very angry if I assume it's there since it is not type-enforced
  if (initialValues && step?.initialValues) {
    initialValues = {
      ...initialValues,
      ...step.initialValues,
    };
  }

  return (
    <OnboardingProvider
      step={stepPosition.key}
      isControlledStep={Boolean(currentStep.onControlledSubmit)}
      skipStep={skipStep}
    >
      <Formik
        {...props.formikProps}
        initialValues={initialValues}
        validate={validate}
        onSubmit={onSubmit}
      >
        {() => (
          <Form>
            <CanSubmitListener step={stepPosition} stepCount={steps.length} />
            <div className={classnames('registration-form', {ionic: platformContext.isIonic})}>
              {platformContext.isIonic && (
                <ProgressbarWrapperComponent
                  onControlledSubmit={onControlledSubmit}
                  stepCount={steps.length}
                  stepPosition={stepPosition}
                  decrementStep={decrementStep}
                  steps={steps}
                />
              )}
              <p className="step-required-field">* Required Field</p>
              <Slide
                timeout={direction ? props.transitionTimeout : 0}
                direction={direction ? (isStepActive ? direction.enter : direction.exit) : 'right'}
                in={isStepActive}
                mountOnEnter
                unmountOnExit
                onExited={onTransitionComplete}
              >
                <div>
                  <currentStep.Component />
                  {platformContext.isIonic && (
                    <MobileOnboardingProgressButton
                      onControlledSubmit={onControlledSubmit}
                      stepPosition={stepPosition}
                    />
                  )}
                </div>
              </Slide>
              {platformContext.isBasic && (
                <OnboardingFooter
                  onBack={decrementStep}
                  stepCount={steps.length}
                  stepPosition={stepPosition}
                  onControlledSubmit={onControlledSubmit}
                />
              )}
            </div>
          </Form>
        )}
      </Formik>
    </OnboardingProvider>
  );
}

Onboarding.defaultProps = {
  basePath: '/onboarding',
  transitionTimeout: 200,
};

const Component = withRouter(Onboarding);
export default function OnboardingWithRouter<Values, Actions>(
  props: OnboardingProps<Values, Actions>
) {
  // @ts-expect-error
  return <Component {...props} />;
}
